import React from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import notificationsApi, { Notification } from '../services/notificationsApi';

interface InternalChatProps {
  className?: string;
}

// Global function to refresh notifications (for use from other components)
let globalRefreshNotifications: (() => void) | null = null;

const InternalChat: React.FC<InternalChatProps> = ({ className = '' }) => {
  const queryClient = useQueryClient();
  const [filter, setFilter] = React.useState<'all' | 'unread' | 'read'>('unread');
  const [page, setPage] = React.useState(1);
  const [newMessage, setNewMessage] = React.useState('');

  // Setup global refresh function
  React.useEffect(() => {
    globalRefreshNotifications = () => {
      queryClient.invalidateQueries(['notifications']);
    };
    return () => {
      globalRefreshNotifications = null;
    };
  }, [queryClient]);

  // Fetch notifications
  const { data: notificationsData, isLoading } = useQuery(
    ['notifications', filter, page],
    () => {
      return notificationsApi.getAll({
        status: filter === 'all' ? undefined : filter,
        page,
        limit: 20,
        sort: 'createdAt',
        order: 'desc'
      });
    },
    {
      refetchInterval: 10000, // Refresh every 10 seconds
      refetchOnWindowFocus: true,
      staleTime: 5000, // Consider data stale after 5 seconds
    }
  );

  // Mark as read mutation
  const markAsReadMutation = useMutation(
    (id: string) => notificationsApi.markAsRead(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notifications']);
      },
    }
  );

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation(
    () => notificationsApi.markAllAsRead(),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notifications']);
      },
    }
  );

  // Send message mutation
  const sendMessageMutation = useMutation(
    (message: string) => {
      return notificationsApi.sendMessage(message);
    },
    {
      onSuccess: () => {
        setNewMessage('');
        queryClient.invalidateQueries(['notifications']);
      },
    }
  );

  const handleMarkAsRead = (notification: Notification) => {
    if (notification.status === 'unread') {
      markAsReadMutation.mutate(notification._id);
    }
  };

  const handleSendMessage = () => {
    if (newMessage.trim()) {
      sendMessageMutation.mutate(newMessage.trim());
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      const minutes = Math.floor(diffInHours * 60);
      return `hace ${minutes} min${minutes !== 1 ? 's' : ''}`;
    } else if (diffInHours < 24) {
      const hours = Math.floor(diffInHours);
      return `hace ${hours} hora${hours !== 1 ? 's' : ''}`;
    } else {
      const days = Math.floor(diffInHours / 24);
      return `hace ${days} día${days !== 1 ? 's' : ''}`;
    }
  };

  const notifications = notificationsData?.notifications || [];
  const unreadCount = notificationsData?.unreadCount || 0;

  return (
    <div className={`bg-white rounded-lg shadow-sm border h-full flex flex-col ${className}`}>
      <div className="border-b border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            💬 Chat Interno
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1 min-w-[20px] text-center">
                {unreadCount}
              </span>
            )}
          </h3>
          {unreadCount > 0 && (
            <button
              onClick={() => markAllAsReadMutation.mutate()}
              disabled={markAllAsReadMutation.isLoading}
              className="text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50"
            >
              Marcar todas como leídas
            </button>
          )}
        </div>

        <div className="flex gap-2">
          {(['unread', 'all', 'read'] as const).map((filterOption) => (
            <button
              key={filterOption}
              onClick={() => {
                setFilter(filterOption);
                setPage(1);
              }}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                filter === filterOption
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {filterOption === 'unread' ? 'Sin leer' : 
               filterOption === 'all' ? 'Todas' : 'Leídas'}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {isLoading ? (
          <div className="text-center text-gray-500 py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
            Cargando mensajes...
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            <span className="text-2xl mb-2 block">📭</span>
            {filter === 'unread' ? 'No hay mensajes sin leer' : 
             filter === 'all' ? 'No hay mensajes' : 'No hay mensajes leídos'}
          </div>
        ) : (
          notifications.map((notification) => {
            const isUserMessage = notification.type === 'user_message';
            const isCancellation = notification.type === 'appointment_cancelled';
            
            return (
              <div
                key={notification._id}
                className={`border-l-4 p-3 rounded-r-lg transition-all hover:shadow-sm cursor-pointer ${
                  isCancellation ? 'border-red-400 bg-red-50' :
                  isUserMessage ? 'border-blue-400 bg-blue-50' : 'border-gray-400 bg-gray-50'
                } ${notification.status === 'unread' ? 'font-medium' : 'opacity-80'}`}
                onClick={() => handleMarkAsRead(notification)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm">
                        {isCancellation ? '🚫' : 
                         isUserMessage ? '�' : '📢'}
                      </span>
                      <h4 className="text-sm font-semibold text-gray-900 truncate">
                        {notification.title}
                      </h4>
                      {notification.status === 'unread' && (
                        <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></span>
                      )}
                    </div>
                    <p className="text-sm text-gray-700 mb-2 leading-relaxed">
                      {notification.message}
                    </p>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{formatDate(notification.createdAt)}</span>
                      {notification.status === 'unread' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkAsRead(notification);
                          }}
                          className="text-blue-600 hover:text-blue-800"
                          title="Marcar como leído"
                        >
                          ✓
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {notification.metadata?.patientId && (
                  <div className="mt-2 pt-2 border-t border-gray-200 text-xs text-gray-600">
                    <span className="font-medium">Paciente:</span> {notification.metadata.patientId.firstName} {notification.metadata.patientId.lastName}
                    {notification.metadata.patientId.phone && (
                      <span className="ml-2">📞 {notification.metadata.patientId.phone}</span>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Message input */}
      <div className="border-t border-gray-200 p-4">
        <div className="flex gap-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Escribe un mensaje al equipo..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
            disabled={sendMessageMutation.isLoading}
          />
          <button
            onClick={handleSendMessage}
            disabled={!newMessage.trim() || sendMessageMutation.isLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
          >
            {sendMessageMutation.isLoading ? 'Enviando...' : 'Enviar'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Export function to refresh notifications from other components
export const refreshNotifications = () => {
  if (globalRefreshNotifications) {
    globalRefreshNotifications();
  }
};

export default InternalChat;